import styled from '@emotion/styled';
import type { fabric } from 'fabric';
import type { FabricEditor } from 'modules/fabric-editor';
import {
  applyMatchLayout,
  authoredBundlesBbox,
  collectSurvivingBundles,
  DEFAULT_MATCH_LAYOUT,
  type LayoutScopeKey,
  MatchLayoutAlignment,
  MatchLayoutAxis,
  MatchLayoutSettings,
  MatchLayoutSnapshot,
  revertMatchLayout,
} from 'modules/manager/domain/match-layout';
import { layoutScopeKeyFor } from 'modules/manager/domain/template-type';
import { selectActiveTemplate, setMatchLayoutLocal, updateMatchLayout } from 'modules/manager/store';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import CommitOptionalNumberField from '../shared/commit-optional-number-field';
import HelpBubble from '../shared/help-bubble';
import InspectorRow from '../shared/inspector-row';
import InspectorSection from '../shared/inspector-section';
import SelectField from '../shared/select-field';
import { LAYOUT_SCOPE_DESCRIPTORS } from './layout-scope-descriptors';

interface CommonSlotLayoutSectionProps {
  editor: FabricEditor;
}

const AXIS_OPTIONS = [
  { value: 'auto', label: 'Авто' },
  { value: 'vertical', label: 'Вертикально' },
  { value: 'horizontal', label: 'Горизонтально' },
];

const ALIGNMENT_OPTIONS = [
  { value: 'center', label: 'Центр' },
  { value: 'start', label: 'Начало' },
  { value: 'end', label: 'Конец' },
  { value: 'distribute', label: 'Распределить' },
];

/**
 * Per-slot layout settings for template types that repeat a bundle — match
 * blocks (MULTIPLE_*), standings rows (TOURNAMENT_TABLE) or player rank slots
 * (TOP_PLAYERS_BY_INDICATORS). Which scope a type uses comes from the
 * template-type registry, and all of the section's Russian copy comes from
 * `LAYOUT_SCOPE_DESCRIPTORS[scopeKey]` — so supporting a new scope is a registry
 * value plus a descriptor entry, not another ternary threaded through the prose.
 */
const CommonSlotLayoutSection: React.FC<CommonSlotLayoutSectionProps> = ({ editor }) => {
  const dispatch = useAppDispatch();
  const template = useAppSelector(selectActiveTemplate);
  const settings = template?.matchLayout ?? DEFAULT_MATCH_LAYOUT;
  const templateType = editor.placeholders?.getTemplateType();
  // Undefined = the type renders one of everything (SCHEDULE/RESULTS), so there
  // is nothing to lay out and the section hides. The `?? 'matchNumber'` below is
  // inert — it only keeps the hooks unconditional.
  const layoutScopeKey = layoutScopeKeyFor(templateType);
  const scopeKey: LayoutScopeKey = layoutScopeKey ?? 'matchNumber';

  const authoredCount = useMemo(() => countAuthoredSlots(template?.objects ?? [], scopeKey), [template?.objects, scopeKey]);

  const [previewCount, setPreviewCount] = useState<number | undefined>(undefined);
  const previewStateRef = useRef<{ snapshot: MatchLayoutSnapshot; hidden: fabric.Object[] } | null>(null);

  const revertPreview = useCallback(() => {
    const state = previewStateRef.current;
    if (!state) return;
    revertMatchLayout(state.snapshot);
    for (const obj of state.hidden) obj.set({ visible: true });
    previewStateRef.current = null;
    editor.canvas.requestRenderAll();
  }, [editor]);

  const applyPreview = useCallback(
    (count: number) => {
      revertPreview();
      if (!template) return;
      const authoredRegion = authoredBundlesBbox(template.objects ?? [], scopeKey);
      if (!authoredRegion) return;
      editor.canvas.discardActiveObject();
      const hidden: fabric.Object[] = [];
      for (const obj of editor.canvas.getObjects()) {
        const n = (obj.data as Record<string, number | undefined> | undefined)?.[scopeKey];
        if (typeof n === 'number' && n > count) {
          obj.set({ visible: false });
          hidden.push(obj);
        }
      }
      const surviving = collectSurvivingBundles(
        editor.canvas.getObjects().filter((o) => o.visible !== false),
        scopeKey,
      );
      const snapshot = applyMatchLayout({ surviving, authoredRegion, settings });
      previewStateRef.current = { snapshot, hidden };
      editor.canvas.requestRenderAll();
    },
    [editor, settings, template, revertPreview, scopeKey],
  );

  useEffect(() => {
    if (previewCount === undefined || previewCount >= authoredCount || authoredCount === 0) {
      revertPreview();
      return;
    }
    applyPreview(previewCount);
  }, [previewCount, authoredCount, settings, applyPreview, revertPreview]);

  useEffect(() => {
    const off = editor.on('selection:changed', (e) => {
      if (e.active.length > 0 && previewStateRef.current) {
        setPreviewCount(undefined);
      }
    });
    return off;
  }, [editor]);

  useEffect(() => () => revertPreview(), [revertPreview]);

  const commitSettings = useCallback(
    (next: MatchLayoutSettings) => {
      if (!template) return;
      dispatch(setMatchLayoutLocal(next));
      dispatch(updateMatchLayout({ id: template.id, matchLayout: next }));
    },
    [dispatch, template],
  );

  if (!layoutScopeKey || !template) return null;

  const d = LAYOUT_SCOPE_DESCRIPTORS[layoutScopeKey];

  const enabledHelp = (
    <span>
      <b>Выключено</b> (по умолчанию): пустые {d.unitPlural} скрываются, а оставшиеся {d.unitPlural} остаются ровно там, где их
      разместили при вёрстке шаблона — если пропали, например, последние {d.unitPlural}, в конце области останется пустое место.
      <br />
      <br />
      <b>Включено</b>: оставшиеся {d.unitPlural} дополнительно сдвигаются внутри исходной области (где были нарисованы все{' '}
      {d.unitPlural} шаблона) по правилам «Ось» и «Выравнивание» ниже — так пустое место убирается или переносится в предсказуемое
      место. {d.unitPluralCap} определяются автоматически по {d.autoSource}, вручную ничего указывать не нужно.
    </span>
  );

  const axisHelp = (
    <span>
      Направление, вдоль которого двигаются оставшиеся {d.unitPlural}, когда «Включено».
      <br />
      <br />
      <b>Авто</b> — направление подбирается автоматически по форме исходной области: если она выше, чем шире, {d.unitPlural} двигаются
      по вертикали, иначе — по горизонтали. Подходит для большинства случаев.
      <br />
      <b>Вертикально</b> — {d.unitPlural} двигаются только вверх/вниз, их горизонтальное положение не меняется. Выбирайте, если в
      шаблоне {d.unitPlural} расположены {d.stackedAdverbial}.
      <br />
      <b>Горизонтально</b> — {d.unitPlural} двигаются только влево/вправо, их вертикальное положение не меняется. Выбирайте, если в
      шаблоне {d.unitPlural} расположены в ряд.
    </span>
  );

  const alignmentHelp = (
    <span>
      Где внутри исходной области (вдоль выбранной оси) окажутся оставшиеся {d.unitPlural} — то есть куда «уедет» пустое место,
      оставшееся от скрытых {d.unitPluralGen}.
      <br />
      <br />
      <b>Начало</b> — {d.unitPlural} прижимаются к верхнему (или левому — при горизонтальной оси) краю области, пустое место остаётся в
      конце (снизу/справа).
      <br />
      <b>Конец</b> — {d.unitPlural} прижимаются к нижнему (или правому) краю области, пустое место остаётся в начале (сверху/слева).
      <br />
      <b>Центр</b> — группа {d.unitPluralGen} центрируется в области, пустое место делится поровну между началом и концом.
      <br />
      <b>Распределить</b> — {d.unitPlural} равномерно растягиваются по всей области от края до края, пустого места не остаётся вовсе
      (нужно минимум 2 оставшихся {d.unitCountedTwo} — при {d.unitSingularPrep} работает как «Центр»).
    </span>
  );

  return (
    <InspectorSection title={d.sectionTitle} headerAction={<HelpBubble content={d.sectionHelp} />}>
      <InspectorRow label="Включено">
        <ToggleRow>
          <input
            type="checkbox"
            checked={settings.enabled}
            onChange={(e) => commitSettings({ ...settings, enabled: e.target.checked })}
          />
          <HelpBubble content={enabledHelp} />
        </ToggleRow>
      </InspectorRow>
      <InspectorRow label="Ось">
        <FieldRow>
          <SelectField
            value={settings.axis}
            onChange={(v) => commitSettings({ ...settings, axis: v as MatchLayoutAxis })}
            options={AXIS_OPTIONS}
          />
          <HelpBubble content={axisHelp} />
        </FieldRow>
      </InspectorRow>
      <InspectorRow label="Выравнивание">
        <FieldRow>
          <SelectField
            value={settings.alignment}
            onChange={(v) => commitSettings({ ...settings, alignment: v as MatchLayoutAlignment })}
            options={ALIGNMENT_OPTIONS}
          />
          <HelpBubble content={alignmentHelp} />
        </FieldRow>
      </InspectorRow>
      <Divider />
      <InspectorRow label="Предпросмотр">
        <FieldRow>
          <CommitOptionalNumberField
            value={previewCount}
            placeholder={authoredCount > 0 ? `1..${authoredCount}` : '—'}
            min={1}
            max={authoredCount > 0 ? authoredCount : undefined}
            onCommit={(v) => setPreviewCount(clampPreview(v, authoredCount))}
          />
          <HelpBubble
            content={
              <span>
                Временно рендерит шаблон так, будто передано только N {d.previewUnitGen} — удобно для проверки выравнивания. Не
                сохраняется. При выборе объекта предпросмотр сбрасывается.
              </span>
            }
          />
        </FieldRow>
      </InspectorRow>
    </InspectorSection>
  );
};

export default CommonSlotLayoutSection;

function countAuthoredSlots(objects: readonly unknown[], scopeKey: LayoutScopeKey): number {
  let max = 0;
  for (const raw of objects) {
    const n = (raw as { data?: Record<string, number | undefined> } | undefined)?.data?.[scopeKey];
    if (typeof n === 'number' && n > max) max = n;
  }
  return max;
}

function clampPreview(value: number | undefined, authoredCount: number): number | undefined {
  if (value === undefined) return undefined;
  if (!Number.isFinite(value) || value < 1) return undefined;
  if (authoredCount <= 0) return undefined;
  if (value >= authoredCount) return undefined;
  return Math.floor(value);
}

const ToggleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  width: 100%;
`;

const FieldRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  width: 100%;
  min-width: 0;

  & > :first-child {
    flex: 1;
    min-width: 0;
  }

  & > :last-child {
    flex: 0 0 auto;
  }
`;

const Divider = styled.hr`
  border: none;
  border-top: 1px solid #e5e7eb;
  margin: 4px 0;
`;
